import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';

const PrayerNotification = () => {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [lastNotification, setLastNotification] = useState({});
  const [audio, setAudio] = useState(null);
  const notificationShownRef = useRef({}); // استخدام useRef لتجنب إعادة التشغيل
  const intervalRef = useRef(null); // استخدام useRef للـ interval

  // أحاديث جميلة لكل صلاة
  const hadiths = {
    fajr: [
      "قال رسول الله ﷺ: من صلى الفجر في جماعة فهو في ذمة الله",
      "قال رسول الله ﷺ: ركعتا الفجر خير من الدنيا وما فيها"
    ],
    dhuhr: [
      "قال رسول الله ﷺ: من حافظ على الصلوات الخمس كان له نور وبرهان ونجاة يوم القيامة"
    ],
    asr: [
      "قال رسول الله ﷺ: من صلى العصر لم يزل في صلاة حتى تغرب الشمس"
    ],
    maghrib: [
      "قال رسول الله ﷺ: من صلى المغرب في جماعة كان كقيام ليلة"
    ],
    isha: [
      "قال رسول الله ﷺ: من صلى العشاء في جماعة كان كقيام نصف ليلة"
    ]
  };

  // أسماء الصلوات بالعربية
  const prayerNames = {
    fajr: "الفجر",
    dhuhr: "الظهر", 
    asr: "العصر",
    maghrib: "المغرب",
    isha: "العشاء"
  };

  // جلب مواعيد الصلاة من API
  const fetchPrayerTimes = async () => {
    try {
      const response = await fetch('https://api.aladhan.com/v1/timingsByCity?city=Cairo&country=Egypt&method=4');
      const data = await response.json();
      
      if (data.status === 'OK') {
        setPrayerTimes(data.data.timings);
      }
    } catch (error) {
      // silent error handling
    }
  };

  // تحويل الوقت إلى دقائق للمقارنة
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // الحصول على اسم الصلاة الحالية
  const getCurrentPrayer = () => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    // فحص الصلوات الحقيقية فقط إذا كان prayerTimes متوفر
    if (!prayerTimes) {
      return null;
    }
    
    const prayerTimesInMinutes = {
      fajr: timeToMinutes(prayerTimes.Fajr),
      dhuhr: timeToMinutes(prayerTimes.Dhuhr),
      asr: timeToMinutes(prayerTimes.Asr),
      maghrib: timeToMinutes(prayerTimes.Maghrib),
      isha: timeToMinutes(prayerTimes.Isha)
    };

    const prayers = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
    
    for (let prayer of prayers) {
      const prayerTime = prayerTimesInMinutes[prayer];
      
      // إذا كان الوقت الحالي قريب من وقت الصلاة (خلال 5 دقائق)
      if (Math.abs(currentTime - prayerTime) <= 5) {
        return prayer;
      }
    }
    
    return null;
  };

  // دالة تشغيل الصوت من ملف MP3
  const playPrayerVoice = (prayerName) => {
    try {
      // التحقق من دعم المتصفح للصوت
      if (!window.Audio) {
        return; // لا صوت إذا لم يدعم المتصفح Audio
      }
      
      // إيقاف الصوت السابق إذا كان يعمل
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      
      // استخدام ملف صوتي خاص بكل صلاة
      const voiceFile = `/audio/${prayerName}.mp3`;
      const newAudio = new Audio(voiceFile);
      newAudio.volume = 0.8; // مستوى الصوت 80%
      
      // إضافة event listeners للتأكد من تحميل الصوت
      newAudio.addEventListener('error', (e) => {
        // لا شيء - لا صوت إذا لم يوجد الملف
      });
      
      // تشغيل الصوت
      newAudio.play().then(() => {
        setAudio(newAudio);
      }).catch((error) => {
        // لا شيء - لا صوت إذا فشل التشغيل
      });
      
    } catch (error) {
      // لا شيء - لا صوت في حالة الخطأ
    }
  };

  // عرض تنبيه الصلاة
  const showPrayerNotification = (prayerName) => {
    const hadithArray = hadiths[prayerName];
    const randomHadith = hadithArray[Math.floor(Math.random() * hadithArray.length)];
    const title = `🕌 حان الآن موعد صلاة ${prayerNames[prayerName]}`;
    const text = randomHadith;
    
    // إيقاف الفيديو والخروج من وضع ملء الشاشة قبل عرض الإشعار
    if (window.stopVideoAndExitFullscreen) {
      window.stopVideoAndExitFullscreen();
    }
    
    // تشغيل فويس الصلاة المناسب
    playPrayerVoice(prayerName);
    
    try {
      Swal.fire({
        title: title,
        text: text,
        icon: 'info',
        confirmButtonText: 'اللهم تقبل',
        confirmButtonColor: '#28a745',
        background: '#f8f9fa',
        allowOutsideClick: false,
        allowEscapeKey: false,
        timer: 10000, // إخفاء تلقائي بعد 10 ثواني
        timerProgressBar: true, // شريط تقدم للوقت
        customClass: {
          title: 'prayer-notification-title',
          content: 'prayer-notification-content'
        },
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        }
      }).then((result) => {
        // إيقاف الصوت عند إغلاق التنبيه
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
        
        // إعادة تشغيل الفيديو إذا كان في صفحة فيديو
        if (window.resumeVideo) {
          setTimeout(() => {
            window.resumeVideo();
          }, 500);
        }

      }).catch((error) => {
        // إيقاف الصوت في حالة الخطأ
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    } catch (error) {
      // silent error handling
    }
  };

  // فحص مواعيد الصلاة كل 10 ثواني
  useEffect(() => {
    let isMounted = true;
    
    const fetchAndStart = async () => {
      try {
        await fetchPrayerTimes();
        
        if (isMounted) {
          // بدء فحص مواعيد الصلاة بعد جلب المواقيت
          const startPrayerCheck = () => {
            if (!isMounted) return;
            
            intervalRef.current = setInterval(() => {
              if (!isMounted) return;
              
              const currentPrayer = getCurrentPrayer();
              
              if (currentPrayer) {
                const today = new Date().toDateString();
                const prayerKey = `${currentPrayer}_${today}`;
                
                // التحقق من أن هذا التنبيه لم يظهر اليوم
                if (!notificationShownRef.current[prayerKey]) {
                  showPrayerNotification(currentPrayer);
                  notificationShownRef.current[prayerKey] = true;
                  
                  // حفظ في localStorage للتأكد من عدم الظهور مرة أخرى
                  try {
                    localStorage.setItem(`prayer_${prayerKey}`, 'true');
                  } catch (error) {
                    // silent error handling
                  }
                }
              }
            }, 10000);
          };

          // بدء الفحص بعد ثانية واحدة
          const timer = setTimeout(startPrayerCheck, 1000);
          
          // تنظيف عند unmount
          return () => {
            clearTimeout(timer);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
          };
        }
      } catch (error) {
        // silent error handling
      }
    };

    fetchAndStart();

    return () => {
      isMounted = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); // لا يعتمد على أي متغير

  // فحص التنبيهات السابقة عند تحميل الصفحة
  useEffect(() => {
    const today = new Date().toDateString();
    const prayers = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
    
    prayers.forEach(prayer => {
      const prayerKey = `${prayer}_${today}`;
      try {
        const wasShown = localStorage.getItem(`prayer_${prayerKey}`);
        if (wasShown === 'true') {
          notificationShownRef.current[prayerKey] = true;
        }
      } catch (error) {
        // silent error handling
      }
    });
  }, []);

  return null;
};

export default PrayerNotification; 